import { isContentPermission, knownContentPermissions } from '@/lib/keyPermissions';
import type { OperatingMode } from '@/api/types';
import { Checkbox } from '@/components/ui/checkbox';
import { FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';

/**
 * The permission checklist for issuing or editing a key.
 *
 * Without `operatingMode` (an org key) it is one flat list. With it (a partner key) the list is
 * split into "Provisioning" and "Content (operator mode)"; the content group is disabled and
 * explained while the partner is DELEGATED. A permission the key already holds but the owner no
 * longer allows stays visible and can be unchecked, never newly checked.
 */
export function PermissionChecklist({
  value,
  onChange,
  available,
  operatingMode,
  error,
}: {
  value: readonly string[];
  onChange: (next: string[]) => void;
  available: readonly string[];
  operatingMode?: OperatingMode | undefined;
  error?: string | undefined;
}) {
  const toggle = (permission: string, checked: boolean) =>
    onChange(checked ? [...value, permission] : value.filter((p) => p !== permission));

  const item = (permission: string) => {
    const allowed = available.includes(permission);
    const held = value.includes(permission);
    return (
      <div key={permission} className="flex flex-wrap items-center gap-x-2 text-sm">
        <label className="flex items-center gap-2">
          <Checkbox
            checked={held}
            disabled={!allowed && !held}
            onCheckedChange={(checked) => toggle(permission, checked === true)}
          />
          {permission}
        </label>
        {held && !allowed && operatingMode && (
          <span className="text-xs text-destructive">not usable in {operatingMode} mode</span>
        )}
      </div>
    );
  };

  if (!operatingMode) {
    const flat = [...new Set([...available, ...value])].sort();
    return (
      <>
        <FieldLabel>Permissions</FieldLabel>
        <div className="flex flex-col gap-2">{flat.map(item)}</div>
        <FieldError>{error}</FieldError>
      </>
    );
  }

  const all = [...new Set([...available, ...value, ...knownContentPermissions()])].sort();
  const provisioning = all.filter((p) => !isContentPermission(p));
  const content = all.filter(isContentPermission);

  return (
    <>
      <FieldLabel>Permissions</FieldLabel>
      <div role="group" aria-label="Provisioning" className="flex flex-col gap-2">
        <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Provisioning
        </div>
        {provisioning.map(item)}
      </div>
      <div role="group" aria-label="Content (operator mode)" className="flex flex-col gap-2">
        <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Content (operator mode)
        </div>
        {operatingMode === 'DELEGATED' && (
          <FieldDescription>
            Content permissions are unavailable while this partner is in DELEGATED mode. Switch it
            to OPERATOR mode to grant them.
          </FieldDescription>
        )}
        {content.map(item)}
      </div>
      <FieldError>{error}</FieldError>
    </>
  );
}
